import classNames from 'classnames';
import { Dropzone } from 'components/molecules/Dropzone';
import { FileUploadedList } from 'components/molecules/FileUploadedList';
import {
  fileState,
  Status,
} from 'components/molecules/FileUploadedList/FileUploaded.types';
import useKeyValueState from 'hooks/useKeyValueState';
import useParallelScheduler from 'hooks/useParallelScheduler';
import { isEmpty } from 'lodash';
import groupBy from 'lodash/groupBy';
import type { FC, ReactNode } from 'react';
import { useContext, useMemo, useRef } from 'react';
import { flushSync } from 'react-dom';
import {
  expensesAcceptedFileUploaderTypes,
  globalyAcceptedFileUploaderTypes,
  maxFileSize,
} from 'utils/constants';
import type { fileUploaderMeta, FileUploaderProps } from './FileUploader.types';
import { DefaultUploaderContentext, ExpensesUploaderContext } from './helpers';

const DefaultFileUploaderProvider: FC<{ children: ReactNode }> = ({
  children,
}) => {
  const filesStateApi = useKeyValueState();

  return (
    <DefaultUploaderContentext.Provider value={filesStateApi}>
      {children}
    </DefaultUploaderContentext.Provider>
  );
};

export const ExpensesUploaderProvider: FC<{ children: ReactNode }> = ({
  children,
}) => {
  const filesStateApi = useKeyValueState();

  return (
    <ExpensesUploaderContext.Provider value={filesStateApi}>
      {children}
    </ExpensesUploaderContext.Provider>
  );
};

export const FileUploader: FC<
  FileUploaderProps & {
    filesStateApi: ReturnType<typeof useKeyValueState>;
    renderExtra?: (
      processFile: any,
      fileUploadMeta: fileUploaderMeta,
    ) => ReactNode;
  }
> = ({
  onUpload,
  dropzoneProps,
  filesListProps,
  className,
  color,
  afterPatchUpload,
  keepUploadingAfterDestroy,
  onAfterDropAccepted,
  renderUploadedFileItem,
  filesStateApi,
  hideDropzoneWhileUploading,
  filesListContainerClassName,
  asDefault,
  renderUploadList,
  renderExtra,
}) => {
  const fileUploaderMeta = useMemo(
    () => ({
      ...groupBy(filesStateApi.values, 'status'),
      isEmpty: isEmpty(filesStateApi.values),
    }),
    [filesStateApi.values],
  );

  // TODO: rename
  const metaRef = useRef(fileUploaderMeta);
  metaRef.current = fileUploaderMeta;

  const schedule = useParallelScheduler({
    onComplete: () => afterPatchUpload?.(metaRef.current),
    keepAlive: keepUploadingAfterDestroy,
  });
  const uidRef = useRef(0);

  const processFile = (
    file: File,
    key: string,
    options?: Record<string, any>,
  ) => {
    const fileState = {
      key,
      file,
      progress: 5,
      status: Status.waiting,
      result: null,
      options,
    };
    filesStateApi.set(key, fileState);
    schedule(() => {
      filesStateApi.update(key, { status: Status.uploading });
      return onUpload({
        file,
        fileShortType: file.type.split('/').pop() as string,
        onProgress: (progress) => filesStateApi.update(key, { progress }),
        options,
      })
        .then((result) =>
          // TODO: only flush sync if afterPatchUpload is provided
          flushSync(() =>
            filesStateApi.update(key, {
              status: Status.done,
              progress: 100,
              result,
            }),
          ),
        )
        .catch(() =>
          flushSync(() => filesStateApi.update(key, { status: Status.error })),
        );
    });
  };

  const onDropAccepted = (files: File[]) => {
    const processFiles = () =>
      files.forEach((file) => {
        const uid = uidRef.current++;
        processFile(file, `_${uid}`);
      });

    if (onAfterDropAccepted) {
      return onAfterDropAccepted(processFiles);
    }

    return processFiles();
  };

  return (
    <div className={classNames('flex gap-6 h-full', className)}>
      {hideDropzoneWhileUploading && filesStateApi.values.length > 0 ? null : (
        <Dropzone
          color={color || dropzoneProps.color}
          render={(dropzoneMeta) =>
            dropzoneProps.render(dropzoneMeta, fileUploaderMeta, processFile)
          }
          asDefault={asDefault}
          className={classNames('flex-1', dropzoneProps.className)}
          renderWrapperClassName={dropzoneProps.renderWrapperClassName}
          onClick={dropzoneProps.onClick}
          dropOptions={{
            onDropAccepted,
            accept: globalyAcceptedFileUploaderTypes,
            maxSize: maxFileSize,
            ...dropzoneProps.dropOptions,
            ...dropzoneProps.getDropOptions?.(fileUploaderMeta),
          }}
        />
      )}

      {renderExtra?.(processFile, fileUploaderMeta)}

      {filesStateApi.values.length > 0 ? (
        renderUploadList ? (
          renderUploadList(filesStateApi.values as any, fileUploaderMeta)
        ) : (
          <div
            className={classNames(
              'flex flex-1 flex-col px-4 overflow-y-auto pb-10 md:pb-0',
              filesListContainerClassName,
            )}
          >
            <div className="flex flex-1 flex-col">
              {filesListProps?.renderHeader?.(fileUploaderMeta)}
              <div className="flex-1">
                <FileUploadedList
                  color={color || filesListProps.color}
                  className="flex flex-col gap-5"
                  onRetry={processFile}
                  files={filesStateApi.values as fileState[]}
                  renderUploadedFileItem={renderUploadedFileItem}
                />
              </div>
            </div>
            {filesListProps?.renderFooter?.(fileUploaderMeta)}
          </div>
        )
      ) : null}
    </div>
  );
};

const RenderUploader: FC<{
  render: (result: ReturnType<typeof useKeyValueState>) => ReactNode;
  renderExtra?: (
    processFile: any,
    fileUploadMeta: fileUploaderMeta,
  ) => ReactNode;
}> = ({ render, renderExtra }) => {
  const filesStateApi = useContext(DefaultUploaderContentext);

  return <>{render(filesStateApi)}</>;
};

export const GlobalExpensesUploader: FC<
  FileUploaderProps & { renderExtra?: (processFile: any) => ReactNode }
> = (props) => {
  const filesStateApi = useContext(ExpensesUploaderContext);

  return (
    <FileUploader
      {...props}
      filesStateApi={filesStateApi}
      dropzoneProps={{
        dropOptions: { accept: expensesAcceptedFileUploaderTypes },
        ...props.dropzoneProps,
      }}
      renderExtra={props.renderExtra}
    />
  );
};

const FileUploaderWrapper: FC<
  FileUploaderProps & {
    renderExtra?: (
      processFile: any,
      fileUploadMeta: fileUploaderMeta,
    ) => ReactNode;
  }
> = (props) => (
  <DefaultFileUploaderProvider>
    <RenderUploader
      render={(result) => <FileUploader {...props} filesStateApi={result} />}
      renderExtra={props.renderExtra}
    />
  </DefaultFileUploaderProvider>
);

export default FileUploaderWrapper;
